import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconMessage, IconRefresh, IconUserQuestion } from '@tabler/icons-react';
import { BoardChatToolbarButton } from './BoardChatToolbarButton';
import { useBoardChatRealtime } from '../context/BoardChatRealtimeProvider';
import { formatBadge } from '../utils/boardChatUtils';
import '../../canny/HubFeedback.css';
import './BoardChatPanel.css';
import './CustomersOnlyModeShell.css';

export const CUSTOMERS_ONLY_TAB_CHAT = 'chat';
export const CUSTOMERS_ONLY_TAB_CADASTRO = 'cadastro';
export const CUSTOMERS_ONLY_TABS_HOST_ID = 'customers-only-nav-tabs-host';
export const CUSTOMERS_CADASTRO_REFRESH_EVENT = 'painel-os-customers-cadastro-refresh';

async function fetchPendingCadastroCount() {
  try {
    const token = localStorage.getItem('authToken');
    const res = await fetch('/api/vc-usr/users-pending', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success !== false) {
      if (json.count != null) return Number(json.count) || 0;
      if (Array.isArray(json.data)) return json.data.length;
    }
    const resCount = await fetch('/api/vc-usr/users-pending/count', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const jsonCount = await resCount.json().catch(() => ({}));
    if (!resCount.ok || jsonCount.success === false) return null;
    return Number(jsonCount.data?.count) || 0;
  } catch {
    return null;
  }
}

/** Tabs estilo Hub (Chat | Cadastro Customers) para modo exclusivo dual. */
export function CustomersOnlyTabs({
  activeTab,
  onTabChange,
  chatBadge = null,
  cadastroBadge = null,
  onRefreshCadastro,
  refreshing = false,
}) {
  const listRef = useRef(null);
  const chatRef = useRef(null);
  const cadastroRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, transform: 'translateX(0)' });

  useLayoutEffect(() => {
    const updateIndicator = () => {
      const list = listRef.current;
      const activeEl =
        activeTab === CUSTOMERS_ONLY_TAB_CHAT ? chatRef.current : cadastroRef.current;
      if (!list || !activeEl) return;
      setIndicatorStyle({
        width: activeEl.offsetWidth,
        transform: `translateX(${activeEl.offsetLeft}px)`,
      });
    };
    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeTab, chatBadge, cadastroBadge]);

  return (
    <div className="customers-only-nav-cluster">
      <div
        className="hub-feedback-tabs-list customers-only-tabs-list"
        ref={listRef}
        role="tablist"
        aria-label="Chat e Cadastro"
      >
        <span className="hub-feedback-tabs-indicator" style={indicatorStyle} aria-hidden="true" />
        <button
          type="button"
          ref={chatRef}
          role="tab"
          aria-selected={activeTab === CUSTOMERS_ONLY_TAB_CHAT}
          className={`hub-feedback-tabs-trigger${
            activeTab === CUSTOMERS_ONLY_TAB_CHAT ? ' is-active' : ''
          }`}
          onClick={() => onTabChange?.(CUSTOMERS_ONLY_TAB_CHAT)}
        >
          <IconMessage size={13} stroke={1.75} aria-hidden />
          <span>Chat</span>
          {chatBadge ? (
            <span className="bch-badge bch-badge--unread customers-only-tab-badge" aria-hidden>
              {chatBadge}
            </span>
          ) : null}
        </button>
        <button
          type="button"
          ref={cadastroRef}
          role="tab"
          aria-selected={activeTab === CUSTOMERS_ONLY_TAB_CADASTRO}
          className={`hub-feedback-tabs-trigger${
            activeTab === CUSTOMERS_ONLY_TAB_CADASTRO ? ' is-active' : ''
          }`}
          onClick={() => onTabChange?.(CUSTOMERS_ONLY_TAB_CADASTRO)}
        >
          <IconUserQuestion size={13} stroke={1.75} aria-hidden />
          <span>Cadastro Customers</span>
          {cadastroBadge ? (
            <span className="bch-badge bch-badge--unread customers-only-tab-badge" aria-hidden>
              {cadastroBadge}
            </span>
          ) : null}
        </button>
      </div>
      {typeof onRefreshCadastro === 'function' ? (
        <button
          type="button"
          className={`customers-only-refresh-btn${refreshing ? ' is-refreshing' : ''}`}
          onClick={onRefreshCadastro}
          disabled={refreshing}
          aria-label="Atualizar cadastros pendentes"
          data-tooltip="Atualizar Cadastro Customers"
        >
          <IconRefresh size={15} stroke={1.75} aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Shell pós-login quando o usuário é exclusivo de chat e/ou cadastro.
 * underHeader: overlay abaixo do main-header; tabs dual vão para o host na navbar.
 */
export function CustomersOnlyModeShell({
  chatOnly = false,
  cadastroOnly = false,
  underHeader = false,
  onOpenOrderDetails,
  onEncaminharOs,
}) {
  const both = Boolean(chatOnly && cadastroOnly);
  const realtime = useBoardChatRealtime();
  const [tab, setTab] = useState(() =>
    chatOnly ? CUSTOMERS_ONLY_TAB_CHAT : CUSTOMERS_ONLY_TAB_CADASTRO
  );
  const [navTabsHost, setNavTabsHost] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useLayoutEffect(() => {
    if (!both || !underHeader) {
      setNavTabsHost(null);
      return undefined;
    }
    setNavTabsHost(document.getElementById(CUSTOMERS_ONLY_TABS_HOST_ID));
    return undefined;
  }, [both, underHeader]);

  const refreshPendingCount = useCallback(async () => {
    const next = await fetchPendingCadastroCount();
    if (next != null) setPendingCount(next);
    return next;
  }, []);

  useEffect(() => {
    if (!both && !cadastroOnly) return undefined;
    let cancelled = false;
    refreshPendingCount().then((n) => {
      if (!cancelled && n != null) setPendingCount(n);
    });
    return () => {
      cancelled = true;
    };
  }, [both, cadastroOnly, refreshPendingCount]);

  const handleRefreshCadastro = useCallback(async () => {
    setRefreshing(true);
    try {
      window.dispatchEvent(new CustomEvent(CUSTOMERS_CADASTRO_REFRESH_EVENT));
      await refreshPendingCount();
    } finally {
      setRefreshing(false);
    }
  }, [refreshPendingCount]);

  const showChat = (!both && chatOnly) || (both && tab === CUSTOMERS_ONLY_TAB_CHAT);
  const chatBadge = formatBadge(realtime?.unreadCount);
  const cadastroBadge = formatBadge(pendingCount);

  const tabsNode = both ? (
    <CustomersOnlyTabs
      activeTab={tab}
      onTabChange={setTab}
      chatBadge={chatBadge}
      cadastroBadge={cadastroBadge}
      onRefreshCadastro={handleRefreshCadastro}
      refreshing={refreshing}
    />
  ) : null;

  return (
    <div
      className={[
        'customers-only-shell',
        underHeader ? 'customers-only-shell--under-header' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {both && navTabsHost
        ? createPortal(tabsNode, navTabsHost)
        : both
          ? <div className="customers-only-shell-tabs">{tabsNode}</div>
          : null}
      {showChat ? (
        <BoardChatToolbarButton
          chatOnly
          underHeader={underHeader}
          onOpenOrderDetails={onOpenOrderDetails}
          onEncaminharOs={onEncaminharOs}
        />
      ) : (
        <BoardChatToolbarButton
          cadastroOnly
          canAccessCadastro
          underHeader={underHeader}
          onPendingCountChange={setPendingCount}
          onOpenOrderDetails={onOpenOrderDetails}
        />
      )}
    </div>
  );
}
